import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { Drawer } from 'vaul';
import { IconButton } from '~/components/ui/nucleus/Button';
import { scheduleScrollFieldIntoView } from '~/helpers/scrollFieldIntoView';

export type BottomSheetProps = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  padding?: string;
  stacked?: boolean;
  hasDivider?: boolean;
  // Allow passing extra props to Drawer.Content
  contentProps?: React.HTMLAttributes<HTMLDivElement>;
};

export const BottomSheet = ({
  children,
  isOpen,
  onClose,
  title,
  padding = 'p-0',
  stacked = false,
  hasDivider = false,
  contentProps = {},
}: BottomSheetProps) => {
  const fieldFocusTimeoutRef = useRef<number>();

  useEffect(() => {
    return () => window.clearTimeout(fieldFocusTimeoutRef.current);
  }, []);

  const handleFieldFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    window.clearTimeout(fieldFocusTimeoutRef.current);
    fieldFocusTimeoutRef.current = scheduleScrollFieldIntoView(event.target);
  };

  return (
    <Drawer.Root
      aria-describedby={undefined}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay className='fixed inset-0 z-[1200] bg-black/40' />
        <Drawer.Content
          aria-describedby={undefined}
          className={classNames(
            'fixed bottom-0 left-0 right-0 rounded-t-2xl  shadow-lg transition-all duration-300',
            'h-[97vh] max-h-[97vh]',
            'bg-surface-container-bright'
          )}
          style={{
            zIndex: stacked ? 1200 : 1300,
            transform: stacked ? 'translateY(-25px) scale(0.96)' : 'none',
            width: '100%',
            marginTop: '0',
            marginLeft: stacked ? 'auto' : '0',
            marginRight: stacked ? 'auto' : '0',
            left: stacked ? 'auto' : 0,
            right: stacked ? 'auto' : 0,
            overflow: stacked ? 'hidden' : 'undefined',
          }}
          {...contentProps}
        >
          <Drawer.Description className='hidden'>
            Bottom Sheet for {title}
          </Drawer.Description>
          {/* Overlay for stacked state */}
          {stacked && (
            <Drawer.Overlay
              className='pointer-events-none absolute inset-0 z-20 bg-black/40 transition-opacity duration-300'
              aria-hidden='true'
            />
          )}
          <div className='flex h-full flex-col'>
            {/* Handle */}
            <div
              className={classNames(
                'mx-auto mb-1 mt-2 h-1.5 w-12 flex-shrink-0 rounded-full bg-gray-300',
                stacked && 'hidden'
              )}
            />
            {/* Title and close */}
            {title ? (
              <div
                className={classNames(
                  hasDivider && 'border-b',
                  stacked ? 'bg-transparent' : 'bg-surface-container-bright',
                  'sticky top-0 grid h-[70px] grid-cols-[45px_minmax(0,_1fr)_45px] items-center justify-between gap-2 bg-surface-container-bright bg-opacity-90 px-3 backdrop-blur-md'
                )}
              >
                <div></div>
                <div className='flex w-full items-center justify-center'>
                  <Drawer.Title className='max-w-full truncate text-xl font-medium'>
                    {title}
                  </Drawer.Title>
                </div>
                <IconButton
                  type='close'
                  onClick={onClose}
                  ariaLabel='Close popup'
                />
              </div>
            ) : (
              <div
                className={classNames(
                  hasDivider && 'border-b',
                  'flex h-[70px] items-center justify-end px-3'
                )}
              >
                <Drawer.Title className='hidden'>Bottom Sheet</Drawer.Title>
                <IconButton
                  className='ml-auto'
                  type='close'
                  onClick={onClose}
                  ariaLabel='Close popup'
                />
              </div>
            )}
            <div
              className={classNames(
                'flex max-w-full flex-1 scroll-pb-28 scroll-pt-8 flex-col overflow-auto',
                padding
              )}
              onFocusCapture={handleFieldFocus}
            >
              {children}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
